import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Field, Form, Formik } from 'formik';
import { get } from 'lodash';
import { Grid } from '@material-ui/core';
import { object, string } from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../src/i18n';
import SubmitModalLayout from '../SubmitModalLayout';
import { TP } from '../../constants/consts';
import CountryCityBlock from '../CountryCityBlock';
import EditModeCard from '../EditModeCard';
import FormikTextField from '../Fields/TextField';
import {
  getStagesAction,
  getVenuesAction,
  getWholeList,
  updateOne,
  useBufferValueAction,
} from '../../containers/App/actions';
import { VENUES, VENUES_STAGES_ENDPOINT } from '../../constants/endpointConstants';
import FormikAutocompleteSelect from '../Fields/FormikAutocompleteSelect';
import { selectBufferValueData, selectStages, selectVenuesOptions } from '../../containers/App/selectors';

const useStyles = makeStyles(theme => ({
  form: {
    margin: theme.spacing(2),
  },
  fieldsBox: {
    padding: theme.spacing(2),
    margin: 0,
  },
}));

const stageFields = {
  name: {
    name: 'name',
    label: `${TP}.FN_STAGE_NAME`,
  },
  country: {
    name: 'country',
  },
  city: {
    name: 'city',
  },
  venue: {
    name: 'venue',
    label: `${TP}.VENUE_NAME`,
  },
};

const { name, country, city, venue } = stageFields;

const initialValues = incomingData => ({
  [name.name]: incomingData?.name,
  [country.name]: incomingData?.country || null,
  [city.name]: incomingData?.city || null,
  [venue.name]: incomingData?.venue || null,
});

const AddNewStageModal = ({ onClose, incomingData, countriesOptions }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formRef = React.createRef(null);
  const venues = useSelector(selectVenuesOptions());
  const stages = useSelector(selectStages());
  const updatedVenueData = useSelector(selectBufferValueData());

  const getVenueStages = venueId => {
    dispatch(
      getWholeList({
        endpoint: VENUES_STAGES_ENDPOINT(venueId),
        sagaRoutine: getStagesAction,
      }),
    );
  };

  React.useEffect(() => {
    const venueId = incomingData?.venue?.value;
    if (venueId) {
      getVenueStages(venueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingData]);

  const getVenues = (query, cityId) => {
    dispatch(
      getWholeList({
        endpoint: VENUES,
        sagaRoutine: getVenuesAction,
        params: {
          queryParams: {
            city_id: cityId,
            query,
          },
        },
      }),
    );
  };

  const createStage = values => {
    dispatch(
      updateOne({
        endpoint: VENUES,
        sagaRoutine: useBufferValueAction,
        id: get(values, `${venue.name}.value`),
        params: {
          stages: [...get(stages, 'data', []).map(stage => ({ id: stage?.id })), { name: get(values, name.name) }],
        },
        callback: updatedVenue =>
          onClose(
            updatedVenue,
            get(updatedVenue, 'stages').find(s => s?.name === get(values, name.name)),
          ),
        successMessage: `Stage ${get(values, name.name)} was created`,
      }),
    );
  };

  return (
    <SubmitModalLayout
      isOpened
      onClose={onClose}
      title1={t(`${TP}.FN_ADD_NEW_STAGE`)}
      submitFunc={() => {
        get(formRef, 'current.submitForm', () => {})();
      }}
      isLoading={stages?.loading || updatedVenueData?.loading}
    >
      <EditModeCard className={classes.form}>
        <Formik
          initialValues={initialValues(incomingData)}
          onSubmit={createStage}
          validationSchema={AddNewStageSchema}
          innerRef={formRef}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2} className={classes.fieldsBox} alignItems="flex-start" direction="row-reverse">
                <Grid item sm={6} xs={12}>
                  <Field
                    instanceId={venue.name}
                    name={venue.name}
                    component={FormikAutocompleteSelect}
                    label={t(venue.label)}
                    options={venues?.options || []}
                    isDisabled={Boolean(incomingData?.venue)}
                    isClearable
                    onChange={chosenVenue => getVenueStages(chosenVenue?.value)}
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Field name={name.name} component={FormikTextField} label={t(name.label)} />
                </Grid>
                <CountryCityBlock
                  countriesOptions={countriesOptions}
                  formValues={values}
                  setFieldValue={setFieldValue}
                  disabledFields={{
                    country: Boolean(incomingData?.country),
                    city: Boolean(incomingData?.city && incomingData?.country),
                  }}
                  onCityChange={v => {
                    setFieldValue(venue.name, null);
                    if (v?.value) {
                      getVenues('', v?.value);
                    }
                  }}
                />
              </Grid>
            </Form>
          )}
        </Formik>
      </EditModeCard>
    </SubmitModalLayout>
  );
};

AddNewStageModal.propTypes = {
  onClose: PropTypes.func,
  incomingData: PropTypes.shape({
    name: PropTypes.string,
    country: PropTypes.object,
    city: PropTypes.object,
  }),
  countriesOptions: PropTypes.array,
};

AddNewStageModal.defaultProps = {
  countriesOptions: [],
  incomingData: {
    name: '',
    country: null,
    city: null,
    venue: null,
  },
};

export default AddNewStageModal;

const AddNewStageSchema = () =>
  object().shape({
    [name.name]: string()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [country.name]: object().nullable(),
    [city.name]: object().nullable(),
    [venue.name]: object()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
  });
